import React from "react";
import OrderedModuleEditor from "@/components/common/OrderedModuleEditor";
import MediaImageField from "@/components/common/MediaImageField";
import SeoFields from "@/components/seo/SeoFields";
import SeoFormScore from "@/components/seo/SeoFormScore";
import SeoLivePreview from "@/components/seo/SeoLivePreview";
import { bandColor } from "@/components/seo/seoUtils";
import { resolveMediaUrl } from "@/lib/serviceMedia";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <Label className="text-xs text-white/50">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

const tagsToString = (tags) => (Array.isArray(tags) ? tags.join(", ") : (tags || ""));
const stringToTags = (value) =>
  String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export default function Portfolio() {
  return (
    <OrderedModuleEditor
      resource="portfolio"
      singular="project"
      title="Portfolio"
      subtitle="Drag to reorder case studies. Upload covers and edit details in the inspector."
      titleKey="title"
      defaultValues={{
        order: 1,
        title: "",
        slug: "",
        type: "",
        industry: "",
        desc: "",
        cover_image: "",
        services: [],
        status: "completed",
        seo: {
          meta_title: "",
          meta_description: "",
          keywords: [],
          canonical_url: "",
          robots: "index,follow",
          og_title: "",
          og_description: "",
          og_image: "",
          og_type: "website",
          schema: "",
        },
      }}
      getItemLabel={(it, idx) => it.title || `Project ${idx + 1}`}
      renderPreviewItem={(it) => {
        const cover = resolveMediaUrl(it.cover_image);
        return (
          <div className="flex gap-3 items-start">
            {cover ? (
              <img src={cover} alt="" className="w-20 h-14 rounded-lg object-cover shrink-0 bg-white/5" />
            ) : (
              <span className="w-20 h-14 rounded-lg glass flex items-center justify-center text-[10px] text-white/40 shrink-0">
                No cover
              </span>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/40">{it.type || "Untitled type"}</div>
              <div className="text-sm font-medium text-white mt-0.5">{it.title || "Untitled"}</div>
              <p className="text-xs text-white/50 mt-1 line-clamp-2">{it.desc || ""}</p>
              {it.industry ? <div className="text-[11px] text-white/40 mt-1">{it.industry}</div> : null}
              <div className="text-[11px] font-semibold mt-1" style={{ color: bandColor(it.seo_score) }}>
                {it.seo_score == null ? "— SEO" : `${it.seo_score} SEO`}
              </div>
            </div>
          </div>
        );
      }}
      renderInspector={(item, patch, _items, opts = {}) => (
        <div className={opts.fullView ? "grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4" : "space-y-3"}>
          <Field label="Title">
            <Input
              value={item.title || ""}
              onChange={(e) => {
                const title = e.target.value;
                const next = { title };
                if (!item.slug) {
                  next.slug = title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")
                    .slice(0, 80);
                }
                patch(next);
              }}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Slug" hint="URL-friendly id (used in /portfolio/slug)">
            <Input
              value={item.slug || ""}
              onChange={(e) => patch({ slug: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Type / Category" hint="e.g. Website Development, Branding & Advertising">
            <Input
              value={item.type || ""}
              onChange={(e) => patch({ type: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Industry" hint="e.g. Education / Local Business">
            <Input
              value={item.industry || ""}
              onChange={(e) => patch({ industry: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <div className={opts.fullView ? "lg:col-span-2" : undefined}>
            <Field label="Description">
              <Textarea
                value={item.desc || item.description || ""}
                onChange={(e) => patch({ desc: e.target.value })}
                rows={opts.fullView ? 6 : 4}
                className="bg-black/20 border-white/10 text-white"
              />
            </Field>
          </div>
          <div className={opts.fullView ? "lg:col-span-2" : undefined}>
            <MediaImageField
              label="Cover Image"
              value={item.cover_image || ""}
              onChange={(url) => patch({ cover_image: url })}
              folder="portfolio"
              aspectClass={opts.fullView ? "h-40" : "h-28"}
              hint="Shown on portfolio cards. Prefer uploading a durable image."
            />
          </div>
          <Field label="Services Used" hint="Comma-separated, e.g. Logo direction, Campaign creative">
            <Input
              value={tagsToString(item.services)}
              onChange={(e) => patch({ services: stringToTags(e.target.value) })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Status">
            <select
              value={item.status || "completed"}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full bg-black/20 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
            >
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </Field>

          <div className={opts.fullView ? "lg:col-span-2 space-y-4" : "contents"}>
            <SeoFields
              value={item.seo || {}}
              onChange={(seo) => patch({ seo })}
              mediaFolder="portfolio"
              ogTypeDefault="website"
            />
            <SeoFormScore resource="portfolio" values={item} />
            <SeoLivePreview
              path={item.slug ? `/portfolio/${item.slug}` : ""}
              draftSeo={item.seo || {}}
              fallbackTitle={item.title || ""}
              fallbackDescription={item.desc || item.description || ""}
              fallbackImage={resolveMediaUrl(item.cover_image) || ""}
            />
          </div>
        </div>
      )}
    />
  );
}
